import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { LanguageSwitcher } from './LanguageSwitcher';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import type {
  LanguageCode,
  TranslationData,
  TranslationFieldConfig,
} from './translations-data';

/**
 * Props for TranslationsForm component
 */
export interface TranslationLanguage {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

interface TranslationsFormProps {
  fields: TranslationFieldConfig[];
  value: TranslationData;
  onChange: (data: TranslationData) => void;
  defaultLanguage?: string;
  className?: string;
  errors?: Record<string, Record<string, string>>;
  languages?: TranslationLanguage[];
  /** Вариант переключателя языков: auto, inline, compact */
  switcherVariant?: 'inline' | 'compact' | 'auto';
  /** Максимум языков для inline отображения */
  maxInlineLanguages?: number;
  /** Внешнее управление активным языком (controlled mode) */
  activeLanguage?: string;
  /** Callback при смене языка (для внешней синхронизации) */
  onLanguageChange?: (code: string) => void;
}

export function TranslationsForm({
  fields,
  value,
  onChange,
  defaultLanguage = 'ru',
  className,
  errors,
  languages,
  switcherVariant = 'auto',
  maxInlineLanguages = 5,
  activeLanguage: externalActiveLanguage,
  onLanguageChange: externalOnLanguageChange,
}: TranslationsFormProps) {
  // Controlled/uncontrolled pattern
  const [internalActiveTab, setInternalActiveTab] = useState<string>(defaultLanguage);

  const activeTab = externalActiveLanguage ?? internalActiveTab;
  const setActiveTab = (code: string) => {
    if (externalOnLanguageChange) {
      externalOnLanguageChange(code);
    } else {
      setInternalActiveTab(code);
    }
  };

  // Fallback to legacy data if languages not provided
  const displayLanguages: TranslationLanguage[] = languages || [];

  const handleFieldChange = useCallback(
    (lang: string, fieldName: string, fieldValue: string) => {
      const newData = {
        ...value,
        [lang]: {
          ...value[lang as LanguageCode],
          [fieldName]: fieldValue,
        },
      };
      onChange(newData);
    },
    [value, onChange]
  );

  if (displayLanguages.length === 0) {
    return (
      <div className={cn('text-muted-foreground text-sm', className)}>
        Нет доступных языков
      </div>
    );
  }

  const activeLang = displayLanguages.find((l) => l.code === activeTab);

  // Скрываем внутренний LanguageSwitcher если язык управляется извне
  const showInternalSwitcher = !externalActiveLanguage;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Language Switcher - показываем только если нет внешнего управления */}
      {showInternalSwitcher && (
        <LanguageSwitcher
          languages={displayLanguages}
          activeLanguage={activeTab}
          onLanguageChange={setActiveTab}
          fields={fields}
          value={value}
          errors={errors}
          variant={switcherVariant}
          maxInlineLanguages={maxInlineLanguages}
        />
      )}

      {/* Active Language Fields */}
      <div className="space-y-4 pt-2">
        {fields.map((field) => {
          const fieldError = errors?.[activeTab]?.[field.name];
          const fieldValue = value[activeTab as LanguageCode]?.[field.name] ?? '';

          return (
            <div key={field.name} className="space-y-2">
              <Label
                htmlFor={`${activeTab}-${field.name}`}
                className="flex items-center gap-1"
              >
                {field.label}
                {field.required && <span className="text-destructive">*</span>}
              </Label>

              {field.type === 'input' && (
                <Input
                  id={`${activeTab}-${field.name}`}
                  value={fieldValue}
                  onChange={(e) =>
                    handleFieldChange(activeTab, field.name, e.target.value)
                  }
                  placeholder={
                    field.placeholder ??
                    `${field.label} (${activeLang?.nativeName ?? activeTab})`
                  }
                  className={cn(fieldError && 'border-destructive')}
                />
              )}

              {field.type === 'textarea' && (
                <Textarea
                  id={`${activeTab}-${field.name}`}
                  value={fieldValue}
                  onChange={(e) =>
                    handleFieldChange(activeTab, field.name, e.target.value)
                  }
                  placeholder={
                    field.placeholder ??
                    `${field.label} (${activeLang?.nativeName ?? activeTab})`
                  }
                  rows={field.rows ?? 4}
                  className={cn(fieldError && 'border-destructive')}
                />
              )}

              {field.type === 'richtext' && (
                <RichTextEditor
                  key={`${activeTab}-${field.name}`}
                  value={fieldValue}
                  onChange={(html) =>
                    handleFieldChange(activeTab, field.name, html)
                  }
                  placeholder={
                    field.placeholder ??
                    `${field.label} (${activeLang?.nativeName ?? activeTab})`
                  }
                  className={cn(fieldError && 'border-destructive')}
                />
              )}

              {fieldError && (
                <p className="text-sm text-destructive">{fieldError}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
